import { getAutoUpdatedMap } from 'shared/hooks/getAutoUpdatedMap';
import { Badge } from 'features/catalog/useBadges';
import { JOURNEYS, isJourneyCompleted } from 'features/catalog/badge-journeys';
import { liveCatalogBadgeIDs } from 'features/catalog/badge-state';
import { getRarityScoreFromName } from 'features/catalog/rarity';

type SheetScoreProfile = {
  email: string;
  submittedBadgeIDs: string[];
};

/**
 * Sheet paste → the owned set the leaderboard scores.
 * Live catalog only, then the same auto-claim / restriction pass as `Profile.badgesIDs`.
 */
export const expandSheetBadgeIDs = (
  badgeIDs: readonly string[],
  badgesMap: Map<string, Badge>,
  profile: SheetScoreProfile
): string[] => {
  const liveOwned = liveCatalogBadgeIDs([...badgeIDs], badgesMap);
  const enabledBadges = new Map(liveOwned.map((id) => [id, true]));
  const autoUpdatedMap = getAutoUpdatedMap(enabledBadges, badgesMap, {
    submittedBadgeIDs: profile.submittedBadgeIDs,
    email: profile.email,
  });
  return [...autoUpdatedMap.entries()].filter(([, isOn]) => isOn).map(([id]) => id);
};

/** Rarity points plus completed journeys — same total as `Profile.sortScore` for that owned set. */
export const sortScoreForBadgeIDs = (badgeIDs: readonly string[], badgesMap: Map<string, Badge>): number => {
  let badgeScore = 0;
  for (const id of badgeIDs) {
    const badge = badgesMap.get(id);
    if (badge) badgeScore += badge.rarityScore;
  }
  const owned = new Set(badgeIDs);
  return (
    badgeScore +
    JOURNEYS.reduce((acc, journey) => {
      if (!isJourneyCompleted(journey, owned)) return acc;
      return acc + getRarityScoreFromName(journey.rarity);
    }, 0)
  );
};

/** Score a historical sheet row the way the leaderboard scores the current roster. */
export const sortScoreForSheetBadgeIDs = (
  badgeIDs: readonly string[],
  badgesMap: Map<string, Badge>,
  profile: SheetScoreProfile
): number => sortScoreForBadgeIDs(expandSheetBadgeIDs(badgeIDs, badgesMap, profile), badgesMap);
